import { create } from 'zustand';

/**
 * A "Répondre" request sent by the Documents card to the Email card
 * (mahhp 22227): reply to a received e-mail of the claim's GED.
 *
 * `idAdoc` is the e-mail's GED id (`ID_ADOC`, as a string) — the option id the
 * Email card's "En réponse à" select uses (`WS2_GS_SEL_EMAIL_RECU`). `label` is
 * the document label (`LIB_ADOC`), the fallback option label while that list
 * is still loading or when it does not list the e-mail.
 */
export interface EmailReplyRequestInput {
  /** The claim the e-mail belongs to — an Email card only applies its own claim's requests. */
  idSin: string;
  idAdoc: string;
  label: string;
}

export interface EmailReplyRequest extends EmailReplyRequestInput {
  /**
   * Unique per request (monotonic, never reused), so a consumer can tell a new
   * request from one it already applied, and clear exactly the one it took.
   */
  nonce: number;
}

/**
 * "This task was just closed" signal (22862 follow-up): set by the « Tâches »
 * card after its inline "Clôturer" succeeded, so the "Clôturer la tâche" card
 * of that same task can switch to its closed state.
 */
export interface ClosedTaskSignal {
  /** `ID_EVENT` of the closed task, as a string (the `taskId` route param). */
  idEvent: string;
  /**
   * Unique per close (monotonic, never reused), so two closes of the same task
   * id are still two distinct signals.
   */
  nonce: number;
}

/**
 * A "Modifier l'intervenant" request (mahhp 22919): open the « Modifier un
 * intervenant » card on one intervenant of the claim — e.g. from the pencil
 * of an intervenants table row (mahhp 22538).
 */
export interface EditIntervenantRequest {
  /** Claim-intervenant id (`ID_SINP`) — the `VAL` of `WS2_GS_SEL_IDSINP`. */
  idSinp: number;
  /**
   * Unique per request (monotonic, never reused), so a consumer can tell a new
   * request from one it already applied — two requests for the same
   * intervenant are still two requests — and clear exactly the one it took.
   */
  nonce: number;
}

/**
 * Claim-level cross-card signals shared by the claim view's cards.
 *
 * A card that changes claim data bumps the matching version; a card that shows
 * data derived from it subscribes to that version and re-fetches. No props, no
 * page wiring — same cross-card pattern as `commentsPanelStore` /
 * `aiChatStore`.
 *
 * - `intervenantsVersion`: bumped by `IntervenantForm` once an intervenant
 *   has been added; `EmailCard` re-fetches its "À" / "CC" recipient
 *   suggestions on it (mahhp 22850).
 * - `tasksVersion`: bumped whenever a task of the claim is created or closed —
 *   by `CreateTaskCard` and `CloseTaskCard` on success, and by the « Tâches »
 *   card's own "Clôturer" row action; `ClaimTasksCard` re-fetches the claim's
 *   task list on it (mahhp 22862).
 * - `lastClosedTask`: the last task closed by the « Tâches » card's inline
 *   "Clôturer" (set by `markTaskClosed` right before `bumpTasks`). The
 *   "Clôturer la tâche" card of that task (`CloseTaskCard`, `taskId ===
 *   idEvent`) enters its "(Tâche clôturée)" state on it — no API call; a
 *   signal for another task id is ignored.
 * - `comptabiliteVersion`: bumped when a movement is recorded on the claim —
 *   by `SettlementCard` (règlement) and `RecoveryCard` (recours) on success;
 *   `ComptabiliteCard` re-fetches the claim's movements on it (22853
 *   follow-up).
 * - `emailReplyRequest`: the pending "Répondre" request (mahhp 22227), set by
 *   the Documents card's "Répondre" button (or, for a click in the Documents
 *   popout window, by the claim view relaying the popout's BroadcastChannel
 *   message) and consumed by `EmailCard`, which opens itself in reply to that
 *   e-mail. It stays pending until an Email card takes it, so a request made
 *   before the card is mounted (or while it shows its "sent" recap) is still
 *   applied.
 * - `editIntervenantRequest`: the pending "Modifier l'intervenant" request
 *   (mahhp 22919), set through `requestEditIntervenant(idSinp)` — e.g. by the
 *   pencil of an intervenants table row — and consumed by
 *   `EditIntervenantCard`, which selects that intervenant in its
 *   « Intervenant » select (re-fetching the options when it is not listed
 *   yet), loads it and leaves its "modified" recap for the edit form. It stays
 *   pending until an edit card takes it, so a request made while the card is
 *   not mounted (e.g. hidden by a rule, then injected by the launcher) is
 *   still applied. Pair it with `focusCardAnchor('claimflow.editIntervenantCard')`
 *   (`useActionInjection.ts`) to scroll to + expand + highlight the card.
 */
interface ClaimDataState {
  intervenantsVersion: number;
  bumpIntervenants: () => void;
  tasksVersion: number;
  /** Signal that the claim's task list changed (a task was created or closed). */
  bumpTasks: () => void;
  lastClosedTask: ClosedTaskSignal | null;
  /** Signal that the task `idEvent` was just closed (replaces the previous signal). */
  markTaskClosed: (idEvent: string) => void;
  comptabiliteVersion: number;
  /** Signal that the claim's accounting movements changed (a règlement / recours was recorded). */
  bumpComptabilite: () => void;
  emailReplyRequest: EmailReplyRequest | null;
  /** Ask the Email card to reply to a received e-mail (replaces any pending request). */
  requestEmailReply: (request: EmailReplyRequestInput) => void;
  /** Clear the pending request — only while it still is the one with this `nonce`. */
  consumeEmailReply: (nonce: number) => void;
  editIntervenantRequest: EditIntervenantRequest | null;
  /**
   * Ask the « Modifier un intervenant » card to edit the intervenant `idSinp`
   * (`ID_SINP`) — replaces any pending request.
   */
  requestEditIntervenant: (idSinp: number) => void;
  /** Clear the pending edit request — only while it still is the one with this `nonce`. */
  consumeEditIntervenant: (nonce: number) => void;
}

// Monotonic for the whole session — never reset (not even when a test resets
// the store state), so a nonce is never reused for another request.
let lastEmailReplyNonce = 0;
// Same guarantee for the closed-task signals.
let lastClosedTaskNonce = 0;
// Same guarantee for the edit-intervenant requests.
let lastEditIntervenantNonce = 0;

export const useClaimDataStore = create<ClaimDataState>((set) => ({
  intervenantsVersion: 0,
  bumpIntervenants: () => {
    set((s) => ({ intervenantsVersion: s.intervenantsVersion + 1 }));
  },
  tasksVersion: 0,
  bumpTasks: () => {
    set((s) => ({ tasksVersion: s.tasksVersion + 1 }));
  },
  lastClosedTask: null,
  markTaskClosed: (idEvent) => {
    lastClosedTaskNonce += 1;
    set({ lastClosedTask: { idEvent, nonce: lastClosedTaskNonce } });
  },
  comptabiliteVersion: 0,
  bumpComptabilite: () => {
    set((s) => ({ comptabiliteVersion: s.comptabiliteVersion + 1 }));
  },
  emailReplyRequest: null,
  requestEmailReply: ({ idSin, idAdoc, label }) => {
    lastEmailReplyNonce += 1;
    set({ emailReplyRequest: { idSin, idAdoc, label, nonce: lastEmailReplyNonce } });
  },
  consumeEmailReply: (nonce) => {
    set((s) => (s.emailReplyRequest?.nonce === nonce ? { emailReplyRequest: null } : s));
  },
  editIntervenantRequest: null,
  requestEditIntervenant: (idSinp) => {
    lastEditIntervenantNonce += 1;
    set({ editIntervenantRequest: { idSinp, nonce: lastEditIntervenantNonce } });
  },
  consumeEditIntervenant: (nonce) => {
    set((s) =>
      s.editIntervenantRequest?.nonce === nonce ? { editIntervenantRequest: null } : s,
    );
  },
}));
